import type { ColumnDef } from "@tanstack/react-table";

import { DataTable } from "@/components/data";

import type { HealthStatus } from "../../api";
import { Chip, type ChipTone } from "../../chips";
import { TabPage } from "../../PageFrame";
import { TABS } from "../../routes";
import type { PageProps } from "../index";
import { MONTHS, Note, PageHead } from "../kit";

export interface HealthItem {
  tab: string;
  status: HealthStatus;
  checked_at: string | null;
  reason: string | null;
}

export interface HealthData {
  items: HealthItem[];
  total: number;
  failing: number;
}

export interface HealthRow {
  key: string;
  page: string;
  status: HealthStatus;
  checkedAt: string | null;
  reason: string | null;
}

export const STATUS_CHIP: Record<HealthStatus, { label: string; tone: ChipTone }> = {
  ok: { label: "Passed", tone: "info" },
  failed: { label: "Failed", tone: "warn" },
  unchecked: { label: "Not checked", tone: "muted" },
};

/** One row per page in tab order, without View Health itself. A page C1 does not report reads "Not checked". */
export function healthRows(items: HealthItem[]): HealthRow[] {
  const byTab = new Map(items.map((item) => [item.tab, item]));
  return TABS.filter((tab) => tab.tabKey !== "view_health" && tab.source !== "pfc").map((tab) => {
    const item = byTab.get(tab.apiTab);
    return {
      key: tab.tabKey,
      page: `${tab.group} › ${tab.label}`,
      status: item?.status ?? "unchecked",
      checkedAt: item?.checked_at ?? null,
      reason: item?.reason ?? null,
    };
  });
}

export const failingCount = (rows: HealthRow[]) => rows.filter((row) => row.status === "failed").length;

/** The line above the table: nothing while a page is failing, the all-clear only when every page passed. */
export function healthNote(rows: HealthRow[]): string | null {
  if (failingCount(rows) > 0) return null;
  const unchecked = rows.filter((row) => row.status === "unchecked").length;
  if (unchecked > 0) return `${unchecked} of ${rows.length} pages have not been checked yet.`;
  return `All ${rows.length} pages passed their last check.`;
}

/** "24 Sep 2026, 16:55" in the viewer's local time. */
export function checkedAtLabel(iso: string | null): string {
  if (!iso) return "—";
  const date = new Date(iso);
  const two = (value: number) => String(value).padStart(2, "0");
  return `${date.getDate()} ${MONTHS[date.getMonth()]} ${date.getFullYear()}, ${two(date.getHours())}:${two(date.getMinutes())}`;
}

export const HEALTH_COLUMNS: ColumnDef<HealthRow, unknown>[] = [
  { id: "page", header: "Page", cell: ({ row }) => row.original.page },
  {
    id: "status",
    header: "Status",
    cell: ({ row }) => <Chip tone={STATUS_CHIP[row.original.status].tone}>{STATUS_CHIP[row.original.status].label}</Chip>,
  },
  { id: "checked", header: "Last check", meta: { align: "right", className: "whitespace-nowrap" }, cell: ({ row }) => checkedAtLabel(row.original.checkedAt) },
  { id: "reason", header: "Reason", cell: ({ row }) => row.original.reason ?? "—" },
];

export function ViewHealthBody({ data }: { data: HealthData }) {
  const rows = healthRows(data.items);
  const note = healthNote(rows);
  return (
    <>
      {note ? <Note>{note}</Note> : null}
      <div className="overflow-x-auto">
        <DataTable<HealthRow>
          columns={HEALTH_COLUMNS}
          rows={rows}
          page={{ pageIndex: 0, pageSize: Math.max(1, rows.length), rowCount: rows.length }}
          onPageChange={() => {}}
          density="catalogue"
          caption="Last data check per page"
          wrap
        />
      </div>
    </>
  );
}

export function ViewHealthPage({ tab, areaName }: PageProps) {
  return (
    <TabPage<HealthData>
      tab={tab}
      areaName={areaName}
      head={(data) => {
        const failing = data ? failingCount(healthRows(data.items)) : 0;
        return (
          <PageHead
            title={tab.label}
            sub={data ? "Every page is re-checked when funnel data changes" : undefined}
            kpi={data ? { label: "Failing", value: failing, danger: failing > 0 } : null}
          />
        );
      }}
    >
      {(data) => <ViewHealthBody data={data} />}
    </TabPage>
  );
}
